import { StoryData } from 'storyblok-js-client';
import { StoryblokRichtext } from 'storyblok-rich-text-react-renderer-ts';

import { includes } from '../strings';

export const fetchStories = async (options: {
  byUuids?: string;
  bySlugs?: string;
  excludingFields?: string;
  locale?: string;
  page: number;
  perPage: number;
  preview?: boolean;
  sortBy?: string;
  startsWith?: string;
}) => {
  try {
    const res = await fetch('/api/storyblok/stories', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(options),
    });
    const { data } = await res.json();
    return data.stories;
  } catch (err) {
    return [];
  }
};

export type PreviewItem = {
  full_slug: string;
  intro: StoryblokRichtext | string | null;
  image: string | null;
  tag: string | null;
  title: string | null;
  uuid: string;
};

export const mapStoriesToPreviewItems = (
  stories: StoryData[],
  term: string,
): PreviewItem[] => {
  const orNull = (value?: string) => value || null;

  const items = (stories || []).reduce((acc, curr) => {
    switch (curr.content.component) {
      case 'artist': {
        if (curr.content.excludeFromIndex) {
          return acc;
        }
        return [
          ...acc,
          {
            full_slug: curr.full_slug,
            intro: orNull(curr.content.intro),
            image: orNull(curr.content.thumbnail?.filename),
            tag: 'Artist',
            title: orNull(curr.content.name),
            uuid: curr.uuid,
          },
        ];
      }
      case 'franchise': {
        return [
          ...acc,
          {
            full_slug: curr.full_slug,
            intro: orNull(curr.content.subtitle),
            image: orNull(curr.content.poster?.filename),
            tag: orNull(curr.content.tag),
            title: orNull(curr.content.title),
            uuid: curr.uuid,
          },
        ];
      }
      case 'events': {
        return [
          ...acc,
          {
            full_slug: curr.full_slug,
            intro: orNull(curr.content.description),
            image: orNull(curr.content.image?.filename),
            tag: 'Event',
            title: orNull(curr.content.title),
            uuid: curr.uuid,
          },
        ];
      }
      case 'product': {
        if (curr.content.excludeFromIndex || curr.content.isPrivateOffer) {
          return acc;
        }
        return [
          ...acc,
          {
            full_slug: curr.full_slug,
            intro: orNull(curr.content.details?.[0]?.description),
            image: orNull(curr.content.thumbnail?.filename),
            tag: curr.content.medium?.[0]
              ? `common:mediums.${curr.content.medium?.[0]}`
              : null,
            title: orNull(curr.content.title),
            uuid: curr.uuid,
          },
        ];
      }
      default:
        return acc;
    }
  }, []);

  const sorted = items.sort((a, b) => {
    const termInTitle = includes(a.title, term) && !includes(b.title, term);
    const isAnInsight =
      includes(a.full_slug, 'insights') && !includes(b.full_slug, 'insights');
    return termInTitle || isAnInsight ? -1 : 0;
  });

  return sorted;
};
